import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import {
  CalendarPlus, Users, TrendingUp, Activity, Clock,
  MapPin, Target, Wallet, Star, Plus, Loader2
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';

const fadeUp = { initial: { opacity: 0, y: 16 }, animate: { opacity: 1, y: 0 } };

const SPORTS = ['Basketball', 'Badminton', 'Football', 'Tennis', 'Volleyball', 'Table Tennis'];
const VENUES = ['Manila Sports Arena', 'Quezon City Courts', 'BGC Active Hub', 'Pasig Recreation Center', 'Makati Sports Complex'];

function StatCard({ icon: Icon, label, value, color = 'blue', delay = 0 }) {
  const colorMap = {
    blue: 'from-blue-600/20 to-blue-600/5 border-blue-500/20 text-blue-400',
    lime: 'from-lime-500/20 to-lime-500/5 border-lime-400/20 text-lime-400',
    pink: 'from-pink-500/20 to-pink-500/5 border-pink-400/20 text-pink-400',
  };
  return (
    <motion.div {...fadeUp} transition={{ delay: delay * 0.08 }}
      className={`bg-gradient-to-br ${colorMap[color]} border rounded-xl p-4`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Icon size={16} className="opacity-70" />
        <span className="text-xs uppercase tracking-wider opacity-70 font-manrope">{label}</span>
      </div>
      <p className="font-barlow text-2xl font-bold text-white">{value}</p>
    </motion.div>
  );
}

export default function CoachDashboard() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({
    sport: 'Basketball', venue_name: 'Manila Sports Arena',
    start_time: '', duration_minutes: 60, capacity: 10, price: 300
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await api.get('/dashboard/coach');
      setData(res.data);
    } catch (err) {
      console.error('Coach dashboard error:', err);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const createSession = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post('/sessions/create', form);
      setShowCreate(false);
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to create session');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const s = data?.stats || {};
  const w = data?.wallet || {};

  return (
    <div data-testid="coach-dashboard" className="space-y-6">
      <motion.div {...fadeUp} className="flex items-center justify-between">
        <div>
          <h1 className="font-barlow text-2xl md:text-3xl font-bold text-white tracking-tight">Coach Hub</h1>
          <p className="text-slate-400 text-sm font-manrope mt-0.5">Manage sessions & track your performance</p>
        </div>
        <button
          data-testid="create-session-btn"
          onClick={() => setShowCreate(!showCreate)}
          className="flex items-center gap-2 px-4 py-2 rounded-full bg-blue-600 hover:bg-blue-500 text-white text-sm font-bold uppercase tracking-wide transition-all shadow-[0_0_15px_rgba(59,130,246,0.3)]"
        >
          <Plus size={16} /> New Session
        </button>
      </motion.div>

      {/* Create Session Form */}
      {showCreate && (
        <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
          className="bg-slate-950/60 backdrop-blur-xl border border-blue-500/20 rounded-xl p-5"
          data-testid="create-session-form"
        >
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-4">Create New Session</h3>
          <form onSubmit={createSession} className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Sport</Label>
              <select value={form.sport} onChange={e => setForm({...form, sport: e.target.value})}
                className="mt-1 w-full rounded-lg bg-slate-900/50 border border-slate-700 text-white p-2 text-sm"
              >
                {SPORTS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Venue</Label>
              <select value={form.venue_name} onChange={e => setForm({...form, venue_name: e.target.value})}
                className="mt-1 w-full rounded-lg bg-slate-900/50 border border-slate-700 text-white p-2 text-sm"
              >
                {VENUES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Start Time</Label>
              <Input type="datetime-local" value={form.start_time} onChange={e => setForm({...form, start_time: e.target.value})}
                className="mt-1 bg-slate-900/50 border-slate-700 text-white" required
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Duration (min)</Label>
              <Input type="number" value={form.duration_minutes} onChange={e => setForm({...form, duration_minutes: parseInt(e.target.value)})}
                className="mt-1 bg-slate-900/50 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Capacity</Label>
              <Input type="number" value={form.capacity} onChange={e => setForm({...form, capacity: parseInt(e.target.value)})}
                className="mt-1 bg-slate-900/50 border-slate-700 text-white"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Price (PHP)</Label>
              <Input type="number" value={form.price} onChange={e => setForm({...form, price: parseInt(e.target.value)})}
                className="mt-1 bg-slate-900/50 border-slate-700 text-white"
              />
            </div>
            <div className="md:col-span-3 flex gap-3">
              <button type="submit" disabled={creating}
                className="px-6 py-2 rounded-full bg-lime-500 hover:bg-lime-400 text-black font-bold text-sm uppercase tracking-wide disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Create Session'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)}
                className="px-6 py-2 rounded-full border border-white/10 text-slate-300 hover:bg-white/5 text-sm"
              >
                Cancel
              </button>
            </div>
          </form>
        </motion.div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard icon={CalendarPlus} label="Total Sessions" value={s.total_sessions || 0} color="blue" delay={0} />
        <StatCard icon={Users} label="Players Coached" value={s.total_players_coached || 0} color="lime" delay={1} />
        <StatCard icon={TrendingUp} label="Avg Fill Rate" value={`${s.avg_fill_rate || 0}%`} color="blue" delay={2} />
        <StatCard icon={Activity} label="Attendance Rate" value={`${s.attendance_rate || 0}%`} color="lime" delay={3} />
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
        <StatCard icon={Clock} label="Upcoming" value={s.upcoming_sessions || 0} color="blue" delay={4} />
        <StatCard icon={Wallet} label="Balance" value={`${w.balance || 0} $PLAY`} color="lime" delay={5} />
        <StatCard icon={Star} label="No-Shows" value={s.no_shows || 0} color="pink" delay={6} />
      </div>

      {/* Upcoming Sessions */}
      {data?.upcoming_sessions?.length > 0 && (
        <motion.div {...fadeUp} transition={{ delay: 0.3 }}>
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
            <Clock size={16} className="text-blue-400" />
            Upcoming Sessions
          </h3>
          <div className="grid md:grid-cols-2 gap-3">
            {data.upcoming_sessions.map((s, i) => (
              <div key={s.id || i} className="bg-slate-950/60 border border-white/10 rounded-xl p-4 hover:border-blue-500/20 transition-all">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs px-2 py-0.5 rounded-full bg-blue-500/20 text-blue-400 font-bold uppercase">{s.sport}</span>
                  <span className="text-xs text-slate-400">{s.current_bookings}/{s.capacity}</span>
                </div>
                <p className="text-white text-sm flex items-center gap-1.5"><MapPin size={12} className="text-slate-500" />{s.venue_name}</p>
                <p className="text-slate-500 text-xs mt-1">{new Date(s.start_time).toLocaleString()}</p>
                <div className="w-full h-1.5 bg-slate-800 rounded-full mt-2 overflow-hidden">
                  <div className="h-full rounded-full bg-blue-500" style={{ width: `${s.fill_rate}%` }} />
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Recent Sessions */}
      {data?.recent_sessions?.length > 0 && (
        <motion.div {...fadeUp} transition={{ delay: 0.4 }}>
          <h3 className="font-barlow text-sm font-bold text-white uppercase tracking-wider mb-3 flex items-center gap-2">
            <Activity size={16} className="text-lime-400" />
            Recent Sessions
          </h3>
          <div className="bg-slate-950/60 border border-white/10 rounded-xl overflow-hidden">
            {data.recent_sessions.map((s, i) => (
              <div key={s.id || i} className="flex items-center gap-3 px-4 py-3 border-b border-white/5 last:border-0 hover:bg-white/5">
                <div className="w-9 h-9 rounded-lg bg-lime-500/20 flex items-center justify-center text-lime-400 flex-shrink-0">
                  <Target size={16} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-white text-sm">{s.sport} at {s.venue_name}</p>
                  <p className="text-slate-500 text-xs">{new Date(s.start_time).toLocaleDateString()}</p>
                </div>
                <span className="text-xs text-slate-400 font-mono">{s.fill_rate}% filled</span>
              </div>
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
}
